import React from 'react';
import { Flex, keyframes, styled } from '@withjoy/joykit';
import { pxToRem } from '@withjoy/joykit/theme';
import { useTranslation } from '@shared/core';
import { ReactComponent as IconBrandLogoNoColor } from '@assets/icons/icon-brand-logo-no-color.svg';

// Subtle two-step opacity fade — mirrors the visual weight of the original branded loader
// without pulling in canvas + ParticleEffect. Defined via styled-components keyframes so
// the interpolation in `PulsingLogo` below stays inside a tagged template literal (required —
// see styled-components error #12 https://git.io/JUIaE#12 — keyframes cannot be `${pulse}`'d
// inside a plain JS template string or `css={{ ... }}` object prop).
const pulse = keyframes`
  0%, 100% { opacity: 0.55; }
  50% { opacity: 1; }
`;

const PulsingLogo = styled(IconBrandLogoNoColor)`
  width: ${pxToRem(67)};
  height: ${pxToRem(42)};
  animation: ${pulse} 1.4s ease-in-out infinite;
`;

/**
 * Lightweight loader used as the lazy-load fallback for createwedding funnel routes.
 *
 * Renders the Joy logo with a CSS-only pulse animation. Replaces the original
 * `<CreateWeddingPlaceholder>` (gray skeleton) with a branded loader, while staying
 * lighter than `JoyLogoLoaderBase` (which pulls canvas + particle system into the
 * route entry chunk).
 *
 * Carries the same accessible loading semantics the original placeholder did
 * (`role="status"`, `aria-busy`, localized `aria-label`).
 *
 * Only rendered during client-side intra-funnel SPA chunk-loads — never during SSR
 * (`webChunkExtractor.collectChunks` resolves chunks synchronously) or initial hydration
 * (`loadableReady` blocks `hydrate()` until SSR-collected chunks finish loading).
 */
export const CreateWeddingFunnelLoader: React.FC = () => {
  const { tq } = useTranslation('createWedding');
  return (
    <Flex
      width="100%"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      paddingY={8}
      role="status"
      aria-live="polite"
      aria-busy="true"
      aria-label={tq`Loading form`}
    >
      <PulsingLogo aria-hidden="true" />
    </Flex>
  );
};
